/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService } from './base'

export interface Tag {
  id: number
  name: string
  type: 'ETMF' | 'NON_ETMF' | 'REGULATORY_SUBMISSION' | 'SYSTEM'
  system?: boolean
  createdAt: string
  createdBy: {
    id: number
    name: string
  }
  updatedAt: string
  updatedBy: {
    id: number
    name: string
  }
}

export interface TagsResponse {
  elements: Tag[]
  totalElements: number
  totalPages: number
  pageNumber: number
  pageSize: number
}

export interface GetListForTableParams {
  id?: number[]
  type?: string
  pageStart: number
  pageSize: number
  sortBy: string
  sortDesc: boolean
  _noLoadingSpinner?: boolean
  [key: string]: any
}

export const tagsApiService = {
  getListForTable: async (params: GetListForTableParams): Promise<TagsResponse> => {
    return baseApiService.fetch('/api/v1/tags', {
      params
    })
  },

  getOptionsForAsyncPicker: async (
    search: string,
    pageStart: number
  ): Promise<{ options: Array<{ value: number; label: string }>; hasMore: boolean }> => {
    const params = {
      name: search || null,
      pageStart,
      pageSize: 25
    }

    const response = await baseApiService.fetch<TagsResponse>('/api/v2/tags/picker', {
      method: 'GET',
      params
    })

    const options = response.elements.map((item) => ({
      value: item.id,
      label: item.name
    }))

    // Sort options by label case-insensitively
    options.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

    const hasMore = response.elements.length + pageStart < response.totalElements

    return { options, hasMore }
  },

  getById: async (id: number): Promise<Tag> => {
    return baseApiService.fetch(`/api/v1/tags/${id}`)
  },

  create: async (tag: Partial<Tag>): Promise<Tag> => {
    return baseApiService.fetch('/api/v1/tags', {
      method: 'POST',
      body: JSON.stringify(tag)
    })
  },

  update: async (id: number, data: Partial<Tag>): Promise<Tag> => {
    return baseApiService.fetch(`/api/v1/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },

  remove: async (id: number): Promise<void> => {
    return baseApiService.fetch(`/api/v1/tags/${id}`, {
      method: 'DELETE'
    })
  }
}
